import React, { useState, useRef, useEffect } from "react";
import AnswersForm from "./AnswersForm";
import AIRecommendationSlider from "./AIRecommendationSlider";
import Sequences from "./Sequences";
import DiagnosisSection from "./DiagnosisSection";
import api, { sendCombinedChangeCommon } from "./api";

const PainAssessment = ({ patientCard, stype, bodyPartsCount = 1 }) => {
  bodyPartsCount = parseInt(bodyPartsCount) || 1;

  const defaultAnswers = {
    painDuration: "",
    painSeverity: "",
    painType: [],
    radiatingPain: "",
    triggers: [],
    mobilityImpact: "",
    comments: "",
  };

  const createDefaultPart = () => ({
    painside: "",
    grade: "",
    [`painpoint${stype.toLowerCase()}`]: false,
    [`painpoint${stype.toLowerCase()}2`]: false,
    numbness: false,
    stiffness: false,
    answers: { ...defaultAnswers },
  });

  const [dataState, setDataState] = useState(
    bodyPartsCount > 1
      ? Array(bodyPartsCount).fill().map(createDefaultPart)
      : createDefaultPart()
  );

  const [aiContext, setAiContext] = useState(defaultAnswers);
  const [seqChanged, setSeqChanged] = useState(false);
  const [serChanged, setServChanged] = useState(false);
  const [recChanged, setRecomChanged] = useState(false);
  const [updateReqd, setUpdateReqd] = useState(false);
  const [notification, setShowNotification] = useState(false);

  const sseq = useRef(null);
  const sser = useRef(null);
  const srec = useRef(null);

  // ---------------- Fetch Patient Data ----------------
  useEffect(() => {
    if (!patientCard) return;

    api
      .get(`${process.env.REACT_APP_URL}/patients/consultation/${patientCard}`)
      .then((response) => {
        const fetchedData = response.data[stype.toLowerCase()];

        if (bodyPartsCount > 1) {
          let normalized = Array.isArray(fetchedData) ? [...fetchedData] : [];
          for (let i = normalized.length; i < bodyPartsCount; i++) {
            normalized.push(createDefaultPart());
          }
          if (normalized.length > bodyPartsCount)
            normalized = normalized.slice(0, bodyPartsCount);
          setDataState(normalized);
        } else {
          if (fetchedData && typeof fetchedData === "object" && !Array.isArray(fetchedData)) {
            setDataState({ ...createDefaultPart(), ...fetchedData });
          } else setDataState(createDefaultPart());
        }

        sseq.current = response.data.sequences || null;
        sser.current = response.data.services || null;
        srec.current = response.data.recommendations || null;
      })
      .catch((error) => console.error("Error fetching patient data:", error));
  }, [patientCard, stype, bodyPartsCount]);

  // ---------------- Update Answer Fields ----------------
  const updateAnswerField = (index, field, value) => {
    // Update local state
    if (bodyPartsCount > 1) {
      setDataState((prev) => {
        const newArr = [...prev];
        newArr[index] = {
          ...newArr[index],
          answers: { ...newArr[index].answers, [field]: value },
        };
        return newArr;
      });
    } else {
      setDataState((prev) => ({
        ...prev,
        answers: { ...prev.answers, [field]: value },
      }));
    }

    // Update AI panel context
    setAiContext((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleCheckbox = (index, field, checked) => {
    if (bodyPartsCount > 1) {
      setDataState((prev) => {
        const newArr = [...prev];
        newArr[index] = { ...newArr[index], [field]: checked };
        return newArr;
      });
    } else {
      setDataState((prev) => ({ ...prev, [field]: checked }));
    }
  };

  const toggleArrayCheckbox = (index, arrayField, value) => {
    if (bodyPartsCount > 1) {
      setDataState((prev) => {
        const newArr = [...prev];
        const arr = newArr[index].answers[arrayField] || [];
        newArr[index].answers[arrayField] = arr.includes(value)
          ? arr.filter((v) => v !== value)
          : [...arr, value];
        return newArr;
      });
    } else {
      setDataState((prev) => {
        const arr = prev.answers[arrayField] || [];
        return {
          ...prev,
          answers: {
            ...prev.answers,
            [arrayField]: arr.includes(value)
              ? arr.filter((v) => v !== value)
              : [...arr, value],
          },
        };
      });
    }
  };

  // ---------------- Handle Save ----------------
  const handleSave = (e) => {
    e.preventDefault();
    const payload = { data: { formData: dataState, stype } };
    if (seqChanged) payload.data.sequences = sseq.current;
    if (recChanged) payload.data.recommendations = srec.current;
    if (serChanged) payload.data.services = sser.current;
    if (updateReqd) payload.data["updateReqd"] = true;
    sendCombinedChangeCommon(e, stype, patientCard, payload);
  };

  const partLabels = Array(bodyPartsCount)
    .fill()
    .map((_, i) => {
      if (bodyPartsCount === 1) return stype;
      if (bodyPartsCount === 2) return i === 0 ? `Left ${stype}` : `Right ${stype}`;
      return `${stype} ${i + 1}`;
    });

  // ---------------- Render Form for Each Part ----------------
  const renderPartForm = (partData, idx = null) => {
    const prefix = stype.toLowerCase() + (idx !== null ? idx : "");
    const safeAnswers = partData?.answers || {};

    const toggleButtonStyle = (selected) => ({
      padding: "6px 12px",
      borderRadius: "6px",
      border: "1px solid #ccc",
      cursor: "pointer",
      backgroundColor: selected ? "#4caf50" : "#fff",
      color: selected ? "#fff" : "#333",
      marginRight: "8px",
      marginBottom: "6px",
      transition: "all 0.2s",
      minWidth: "80px",
      textAlign: "center",
    });

    const toggleField = (field) => {
      toggleCheckbox(idx, field, !partData[field]);
    };

    return (
      <div
        key={idx !== null ? idx : "single"}
        style={{
          border: "1px solid #ddd",
          padding: 12,
          marginBottom: "20px",
          borderRadius: "8px",
          flex: bodyPartsCount > 1 ? "1 1 48%" : "1 1 100%",
          minWidth: 0,
          boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
        }}
      >
        {idx !== null && (
          <h3 style={{ marginTop: 0, marginBottom: "10px" }}>{partLabels[idx]}</h3>
        )}

        {/* Pain Side */}
        <div style={{ marginBottom: "10px" }}>
          <label style={{ fontWeight: "bold" }}>Pain Side:</label>
          <div style={{ display: "flex", flexWrap: "wrap", marginTop: "6px" }}>
            {["left", "right", "both", "front", "back", "top", "bottom"].map((side) => (
              <button
                key={side}
                type="button"
                onClick={() => toggleCheckbox(idx, "painside", side)}
                style={toggleButtonStyle(partData.painside === side)}
              >
                {side.charAt(0).toUpperCase() + side.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Grade */}
        <div style={{ marginBottom: "10px" }}>
          <label style={{ fontWeight: "bold" }}>Grade:</label>
          <div style={{ display: "flex", flexWrap: "wrap", marginTop: "6px" }}>
            {["g1", "g2", "g3", "g4"].map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => toggleCheckbox(idx, "grade", g)}
                style={toggleButtonStyle(partData.grade === g)}
              >
                {g.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

      {/* Painpoints */}
        <div style={{ marginBottom: "10px" }}>
          <label style={{ fontWeight: "bold" }}>Regions:</label>
          <div style={{ display: "flex", flexWrap: "wrap", marginTop: "6px" }}>
    
        {[`painpoint${stype.toLowerCase()}`, `painpoint${stype.toLowerCase()}2`].map((field, i) => (
          <button
            key={field}
            type="button"
            onClick={() => toggleField(field)}
            style={toggleButtonStyle(partData[field])}
           
          >
            {stype} Region{i === 1 ? " 2" : ""}
          </button>
        ))}
      </div>
      </div>

      {/* Symptoms */}
        <div style={{ marginBottom: "10px" }}>
          <label style={{ fontWeight: "bold" }}>Symptoms:</label>
          <div style={{ display: "flex", flexWrap: "wrap", marginTop: "6px" }}>
    
        {["numbness", "stiffness"].map((field) => (
          <button
            key={field}
            type="button"
            onClick={() => toggleField(field)}
            style={toggleButtonStyle(partData[field])}
          >
            {field.charAt(0).toUpperCase() + field.slice(1)}
          </button>
        ))}
      </div>
      </div>

        {/* Answers Form */}
        <AnswersForm
          answers={safeAnswers}
          onChange={(field, value) => updateAnswerField(idx, field, value)}
          onCheckboxChange={(field, value) => toggleArrayCheckbox(idx, field, value)}
          aiMappingUpdater={(field, value) => {
            // Only comments mapped to AI abbreviations
            if (field === "comments") setAiContext((prev) => ({ ...prev, comments: value }));
          }}
          namePrefix={prefix}
        />

        {/* Sequences */}
        <Sequences
          aiTokens={aiContext}
          aiComments={aiContext.comments}
        />
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <div
        style={{
          position: "fixed",
          top: 0,
          backgroundColor: "white",
          zIndex: 10,
          padding: "10px 20px",
          borderBottom: "1px solid #ccc",
          fontWeight: "bold",
          fontSize: "18px",
          width: "100%",
        }}
      >
        {stype} Pain Assessment
      </div>

      <div
        style={{
          overflowY: "auto",
          flex: 1,
          padding: "20px",
          marginTop: "50px",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: "20px",
        }}
      >
        <form
          style={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            gap: "20px",
            boxSizing: "border-box",
          }}
          onSubmit={handleSave}
        >
          {Array.isArray(dataState)
            ? dataState.map((part, idx) => renderPartForm(part, idx))
            : renderPartForm(dataState)}

          <div style={{ flexBasis: "100%" }}>
            <DiagnosisSection
              filterT={stype}
              patientCard={patientCard}
              onRecomChange={(e, data, comp, toSend) => {
                if (!toSend) srec.current = data;
                setRecomChanged(true);
              }}
              onSeqChange={(e, data, comp, toSend, updateReqd) => {
                if (!toSend) sseq.current = data;
                setSeqChanged(true);
                setUpdateReqd(updateReqd);
              }}
              onServiceChange={(e, data, comp, toSend) => {
                if (!toSend) sser.current = data;
                setServChanged(true);
              }}
              onSeqInProgress={(show) => setShowNotification(show)}
            />
          </div>

          {notification && (
            <button
              className="floating-drawer-button"
              type="button"
              style={{ bottom: "80vh" }}
              onClick={() =>
                alert(
                  "Session is in progress. \n Limited Changes are allowed. \n Consultation remarks can be changed for all the Sequences.\n"
                )
              }
            >
              Session InProgress
            </button>
          )}

          <button
            className="floating-drawer-button"
            type="submit"
            style={{ bottom: "15vh" }}
          >
            📝 Save
          </button>
        </form>
      </div>

      {/* AI Slider Panel */}
      <AIRecommendationSlider aiContext={aiContext} />
    </div>
  );
};

export default PainAssessment;
